import { useEffect, useMemo, useState } from "react";
import type { WallId, WallSquareState, WallStateResponse } from "@gooner/shared";
import { wallIdOfView } from "@gooner/shared";
import { api } from "../lib/api";

/** How often the board re-asks who owns what. Prices decay by the day and takeovers
 *  arrive by ws config push, so this poll only keeps min-bids and reservation badges
 *  from going stale on a screen nobody is touching. */
const POLL_MS = 30_000;

/**
 * A wall's board state, polled while that wall is showing.
 *
 * One poll for the whole screen — eleven tiles read from this map rather than eleven
 * fetches. `enabled: false` (off, or fetch failed) simply renders no buy affordance: the
 * board itself never depends on this hook, only the money chrome does.
 *
 * Keyed on the WALL, not on "a wall is showing": the two walls price their squares apart,
 * so a retarget from one to the other drops the old board at once rather than painting
 * the Community Wall's prices on the Premium Wall for a tick. A view id that is no wall
 * (or null) polls nothing.
 */
export function useWallState(viewId: string | null): {
  wallId: WallId | null;
  enabled: boolean;
  /** Claiming here is a GoonLords Premium privilege. False until the board answers. */
  premiumOnly: boolean;
  /** What every square's price is denominated in. `cash` until the board answers —
   *  the older, dollar-labelled state is the one a stale badge should show. */
  currency: "faps" | "cash";
  squares: Map<string, WallSquareState>;
} {
  const wallId = viewId ? wallIdOfView(viewId) : null;
  const [state, setState] = useState<WallStateResponse | null>(null);

  useEffect(() => {
    setState(null);
    if (!wallId) return;
    let alive = true;
    const pull = () => {
      api
        .wallState(wallId)
        .then((s) => {
          if (alive) setState(s);
        })
        .catch(() => {
          // A failed poll keeps the last board rather than blanking badges; the next
          // tick retries. A screen with no server has bigger problems than min-bids.
        });
    };
    pull();
    const timer = setInterval(pull, POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [wallId]);

  const squares = useMemo(() => {
    const map = new Map<string, WallSquareState>();
    for (const sq of state?.squares ?? []) map.set(sq.regionId, sq);
    return map;
  }, [state]);

  return {
    wallId,
    enabled: state?.enabled ?? false,
    premiumOnly: state?.premiumOnly ?? false,
    currency: state?.currency ?? "cash",
    squares,
  };
}
